import React, { useState, useRef } from 'react';
import { Zap, Activity, History, RotateCcw } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Device } from '../types';

interface DeviceActionMenuProps {
  activeDeviceMenu: string;
  setActiveDeviceMenu: (id: string | null) => void;
  menuPosition: { x: number; y: number; placement?: 'top' | 'right' };
  devices: Device[];
  handleDeviceAction: (deviceId: string, action: string) => void;
}

const HOLD_MS = 5000;

export const DeviceActionMenu: React.FC<DeviceActionMenuProps> = ({
  activeDeviceMenu,
  setActiveDeviceMenu,
  menuPosition,
  devices,
  handleDeviceAction
}) => {
  const device = devices.find(d => d.id === activeDeviceMenu);

  const [rebootProgress, setRebootProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef<number>(0);

  const startHold = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    startRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      const p = Math.min(((Date.now() - startRef.current) / HOLD_MS) * 100, 100);
      setRebootProgress(p);
      if (p >= 100) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setRebootProgress(0);
        handleDeviceAction(String(device!.id), 'reboot');
      }
    }, 50);
  };

  const cancelHold = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRebootProgress(0);
  };

  if (!device) return null;

  const holdLabel = rebootProgress > 0
    ? `${Math.round(rebootProgress / 10) * 10}%`
    : 'segure 5s';

  // 'right' — vertical/sidebar Botoeira: anchor to the side of the clicked row, vertically
  // centered on it, so the popup doesn't open upward over the rows above.
  const isRightPlacement = menuPosition.placement === 'right';

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={() => setActiveDeviceMenu(null)} />
      <div
        className="fixed bg-gray-950 rounded-lg shadow-2xl border border-gray-800 overflow-hidden z-[70] min-w-[200px]"
        style={isRightPlacement
          ? {
              left: `${menuPosition.x}px`,
              top: `${menuPosition.y}px`,
              transform: 'translate(8px, -50%)'
            }
          : {
              left: `${menuPosition.x}px`,
              top: `${menuPosition.y}px`,
              transform: 'translate(-50%, -100%) translateY(-8px)'
            }}
      >
        {/* Device Info Header */}
        <div className="px-3 py-2 bg-black border-b border-gray-800 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-200">{device.name}</span>
          <div className={cn(
            "w-2 h-2 rounded-full",
            device.status === 'online' ? "bg-green-500 animate-pulse" : "bg-red-500"
          )} />
        </div>

        {/* Primary Action */}
        <div className="p-2">
          <button
            onClick={() => handleDeviceAction(String(device.id), 'trigger')}
            className="w-full flex items-center gap-3 px-3 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition-all group"
          >
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <Zap className="h-4 w-4 text-white" />
            </div>
            <div className="text-left">
              <p className="text-xs font-semibold text-white">Acionar Agora</p>
              <p className="text-[10px] text-blue-200">Executar ação imediata</p>
            </div>
          </button>
        </div>

        {/* Secondary Actions */}
        <div className="border-t border-gray-800">
          <button
            onClick={() => handleDeviceAction(String(device.id), 'test')}
            className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-900 transition-colors"
          >
            <Activity className="h-4 w-4 text-gray-500" />
            <span className="text-xs text-gray-400">Testar Dispositivo</span>
          </button>
          <button
            onClick={() => handleDeviceAction(String(device.id), 'history')}
            className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-900 transition-colors border-t border-gray-800"
          >
            <History className="h-4 w-4 text-gray-500" />
            <span className="text-xs text-gray-400">Ver Histórico</span>
          </button>

          {/* Reiniciar — hold 5s to confirm */}
          <button
            onMouseDown={startHold}
            onMouseUp={cancelHold}
            onMouseLeave={cancelHold}
            onTouchStart={startHold}
            onTouchEnd={cancelHold}
            className="relative w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-900 transition-colors border-t border-gray-800 overflow-hidden select-none"
          >
            {/* Hold progress fill */}
            <div
              className="absolute inset-y-0 left-0 bg-orange-500/20 transition-none"
              style={{ width: `${rebootProgress}%` }}
            />
            <RotateCcw className={cn("h-4 w-4 relative z-10", rebootProgress > 0 ? "text-orange-400 animate-spin" : "text-gray-500")} />
            <div className="relative z-10 flex items-center justify-between flex-1">
              <span className={cn("text-xs", rebootProgress > 0 ? "text-orange-300" : "text-gray-400")}>
                Reiniciar Dispositivo
              </span>
              <span className="text-[10px] text-gray-600 ml-2">{holdLabel}</span>
            </div>
          </button>
        </div>
      </div>
    </>
  );
};
